import React, { useState } from 'react';
import { useExploreSearch } from '../../hooks/useExploreSearch';
import './ExploreSurface.css';

/**
 * The search over the map (§1.1), for the narrow MAP view (slice 4c-3).
 *
 * §1.6: "On the map view, an atlas hit **flies the map** to the destination and
 * opens its card. The inline atlas-first results stay for the list view and for
 * a miss, which ends at 'Ask AI'." The inline box is `HeroSearch`, unchanged;
 * this one exists because the map view lowers the sheet, and a reader standing
 * on the map had no way to ask for a place without leaving it.
 *
 * Same hook as the inline box — debounce, staleness, §22.6 analytics and the
 * failure path are ONE definition, not two that agree today.
 */
export const MapSearch: React.FC<{
  /** An atlas hit. `lat`/`lng` are null when the atlas row has no coordinates —
   *  the caller decides the fallback, because flying needs somewhere to fly to. */
  onHit: (slug: string, lat: number | null, lng: number | null, pinned: boolean) => void;
  onPlan: (slug: string) => void;
  onAskAI: (query: string) => void;
}> = ({ onHit, onPlan, onAskAI }) => {
  const [query, setQuery] = useState('');
  const isActive = query.length > 1;
  const { places, plans } = useExploreSearch(query, isActive);

  const shownPlaces = places ?? [];
  const settled = places !== null;
  const pick = (act: () => void) => { setQuery(''); act(); };

  return (
    <div className="explore-map-search">
      <input
        type="search"
        className="explore-map-search__input"
        placeholder="Search places…"
        aria-label="Search places"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {isActive && settled && (
        <div className="explore-map-search__results" role="listbox" aria-label="Places found">
          {shownPlaces.map((d) => (
            <button
              key={d.slug}
              type="button"
              role="option"
              aria-selected="false"
              className="explore-map-search__result"
              onClick={() => pick(() => onHit(d.slug, d.lat, d.lng, d.pinned))}
            >
              {d.where}
            </button>
          ))}
          {plans.map((p) => (
            <button
              key={p.slug}
              type="button"
              role="option"
              aria-selected="false"
              className="explore-map-search__result"
              onClick={() => pick(() => onPlan(p.slug))}
            >
              {p.title}
            </button>
          ))}
          {/* A miss ends at Ask AI (§1.6) — an answer, not a dead end. */}
          {shownPlaces.length === 0 && plans.length === 0 && (
            <button
              type="button"
              role="option"
              aria-selected="false"
              className="explore-map-search__result explore-map-search__ask"
              onClick={() => pick(() => onAskAI(query.trim()))}
            >
              Ask AI about “{query.trim()}” →
            </button>
          )}
        </div>
      )}
    </div>
  );
};
